import React, { useMemo } from 'react';
import Footer, { FooterColumn, SocialMedia, StyleProps } from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/footer';
import { FooterLink } from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/footer/components/column';
import { Reference } from 'types/reference';
import { NextFrontasticImage } from 'frontastic/lib/image/types';
import useSanityPermissionsChecker from 'helpers/hooks/useSanityPermissionsChecker';

interface Props {
  data: any;
  customerGroup: any;
}

const FooterCmsTastic = ({ data, customerGroup }: Props) => {
  const { checkNestedItemPermission, checkItemPermission } = useSanityPermissionsChecker();

  // Get cmsData from data (Sanity)
  const cmsData = data?.cmsData;
  const fields = cmsData?.dataSource?.fields;

  // Helper: Filter items by permissions
  const filterByPermissions = useMemo(
    () => (items: any[]) => items.filter((item) => checkNestedItemPermission(item, customerGroup)),
    [checkNestedItemPermission, customerGroup],
  );

  // Filter columns by section permission only (no recursion into links)
  const filterColumnsBySectionPermission = useMemo(
    () => (columns: any[]) => columns.filter((col) => checkItemPermission(col, customerGroup)),
    [checkItemPermission, customerGroup],
  );

  // Helper: Create Reference object from link data
  const createReference = (linkData: any): Reference =>
    ({
      type: 'link',
      link: linkData?.linkUrl || linkData?.link,
      openInNewWindow: !!linkData?.openInNewTab,
    }) as Reference;

  // Helper: Map link to FooterLink format
  const mapLinkToFooterLink = (link: any): FooterLink => {
    const linkIcon = link?.linkIcon || link?.icon;
    return {
      name: link?.linkName || link?.name,
      icon: linkIcon?.fields?.file?.url || linkIcon?.url || linkIcon,
      reference: createReference(link),
    } as FooterLink;
  };

  // Helper: Get image URL from Sanity structure
  const getImageUrl = (image: any) => image?.fields?.file?.url || image?.url || image;

  // Check top-level permissions first
  const hasTopLevelPermission = useMemo(() => {
    if (fields?.userRoles) {
      return checkNestedItemPermission({ userRoles: fields.userRoles }, customerGroup);
    }
    return true;
  }, [fields?.userRoles, customerGroup, checkNestedItemPermission]);

  // Process columns: section visibility by column permission only; links filtered by their own permission.
  // So "Shop" shows if user has access to the section, with only the links they have access to (e.g. uniforms, not merch).
  const columns = useMemo(() => {
    if (!hasTopLevelPermission) return [];

    return filterColumnsBySectionPermission(fields?.columns || []).map((column: any) => {
      const links = filterByPermissions(column?.links || column?.link || []).map(mapLinkToFooterLink);
      return {
        header: column?.title || column?.header || '',
        links,
      } as FooterColumn;
    });
  }, [fields?.columns, hasTopLevelPermission, filterColumnsBySectionPermission, filterByPermissions]);

  // Process social media with permission checks
  const socialMediaList = useMemo(() => {
    if (!hasTopLevelPermission) return [];

    return filterByPermissions(fields?.socialMedia || []).map((socialMedia: any) => {
      const linkIcon = socialMedia?.linkIcon || socialMedia?.icon;
      return {
        logo: {
          media: {
            file: getImageUrl(linkIcon),
            name: linkIcon?.fields?.file?.fileName || linkIcon?.fileName,
            width: linkIcon?.fields?.file?.details?.image?.width || linkIcon?.width,
            height: linkIcon?.fields?.file?.details?.image?.height || linkIcon?.height,
          },
        } as NextFrontasticImage,
        reference: createReference(socialMedia),
      } as SocialMedia;
    });
  }, [fields?.socialMedia, hasTopLevelPermission, filterByPermissions]);

  // Extract style props from Sanity
  const styleProps = useMemo(() => {
    const backgroundImage = fields?.backgroundImage;
    return {
      backgroundImage: backgroundImage?.fields?.file?.url || backgroundImage?.url || backgroundImage,
      backgroundColor: fields?.backgroundColor,
      fontColor: fields?.fontColor,
    } as StyleProps;
  }, [fields]);

  if (!hasTopLevelPermission) {
    return <></>;
  }

  return (
    <div className="fixed-screen-width lg:relative-width">
      <Footer columns={columns} socialMedia={socialMediaList} styleProps={styleProps} />
    </div>
  );
};

export default FooterCmsTastic;
